import { FiledClaimUiModel } from "../models/filed-case-ui.model";
import {
  BuildDashboardWidgetKpisOptions,
  CaseOverviewWidgetKpi,
  CaseStatusWidgetKpi,
  DashboardWidgetKpis,
  LostOutboundClaimWidgetKpi,
  OpportunityValueWidgetKpi,
  OtherClaimsWidgetKpi,
  StoreProviderReportWidgetKpi,
} from "../models/dashboard-widget-kpi.model";
import { ProviderReportData } from "../models/provider-report.model";
import { UnClaimUiModel } from "../models/unclaim-cases-ui.model";

function calculateLostByType(
  unfilledClaim: UnClaimUiModel,
  type: string,
  field: "amount" | "total",
): number {
  return unfilledClaim.unClaimProviderBreakdownUIModel.reduce((sum, provider) => {
    const match = provider.claimTypeBreakdown.find(
      (item) => item.typeName.toLowerCase() === type.toLowerCase(),
    );
    return sum + (match?.[field] ?? 0);
  }, 0);
}

function calculateOpenAmount(filedClaim: FiledClaimUiModel): number {
  return filedClaim.filedClaimProviderBreakdownUIModel.reduce((sum, provider) => {
    const nonCanceledAmount = (provider.statusBreakdown || []).reduce(
      (subSum, status) =>
        subSum +
        (status.isClosed
          ? 0
          : status.amount -
            (status.cashReimbursementAmount + status.inventoryReimbursementAmount)),
      0,
    );
    return sum + nonCanceledAmount;
  }, 0);
}

function calculateSuccessRate(filedClaim: FiledClaimUiModel): number {
  const summary = filedClaim.summary;
  const totalAmountCases = summary.filedCasesAmount ?? 0;
  if (totalAmountCases === 0) {
    return 0;
  }

  const reimbursedCases =
    (summary.cashReimbursementAmount ?? 0) +
    (summary.inventoryReimbursementAmount ?? 0);
  const rate = (reimbursedCases / totalAmountCases) * 100;
  return Math.round(rate * 100) / 100;
}

function buildCaseOverviewKpi(filedClaim: FiledClaimUiModel): CaseOverviewWidgetKpi {
  const { summary } = filedClaim;
  return {
    filedCasesAmount: summary.filedCasesAmount,
    filedCasesTotal: summary.filedCasesTotal,
    cashReimbursementTotal: summary.cashReimbursementTotal,
    inventoryReimbursementTotal: summary.inventoryReimbursementTotal,
    cashReimbursementAmount: summary.cashReimbursementAmount,
    inventoryReimbursementAmount: summary.inventoryReimbursementAmount,
  };
}

function buildCaseStatusKpi(
  filedClaim: FiledClaimUiModel,
  caseStatuses: BuildDashboardWidgetKpisOptions["caseStatuses"],
): CaseStatusWidgetKpi {
  return {
    openAmount: calculateOpenAmount(filedClaim),
    successRate: calculateSuccessRate(filedClaim),
    statusSummary: filedClaim.summary.statusSumary ?? {},
    caseStatuses,
  };
}

function buildOpportunityValueKpi(
  filedClaim: FiledClaimUiModel,
  unfilledClaim: UnClaimUiModel,
  rangeLabel: string,
): OpportunityValueWidgetKpi {
  const openAmount = calculateOpenAmount(filedClaim);

  const filedNoReimbursementTotal = Object.values(
    filedClaim.summary.statusSumary || {},
  ).reduce((sum, status) => {
    if (!status.isClosed) {
      return sum + status.total;
    }
    return sum;
  }, 0);

  const filedNoReimbursementAmount = Object.values(
    filedClaim.summary.statusSumary || {},
  ).reduce((sum, status) => {
    if (!status.isClosed) {
      const nonReimbursedAmount =
        status.amount -
        (status.cashReimbursementAmount + status.inventoryReimbursementAmount);
      return sum + nonReimbursedAmount;
    }
    return sum;
  }, 0);

  return {
    rangeLabel,
    nonReimbursementTotal: openAmount + unfilledClaim.summary.unclaimedCasesAmount,
    filedNoReimbursementTotal,
    unFiledClaimAmount: unfilledClaim.summary.unclaimedCasesAmount,
    unFiledClaimTotal: unfilledClaim.summary.unclaimedCasesTotal,
    filedNoReimbursementAmount,
  };
}

function buildOtherClaimsKpi(unfilledClaim: UnClaimUiModel): OtherClaimsWidgetKpi {
  const totalAmount = unfilledClaim.unClaimProviderBreakdownUIModel.reduce(
    (sum, provider) => {
      const relevantAmount = provider.claimTypeBreakdown
        .filter(
          (type) =>
            type.typeName !== "Lost_Inbound" && type.typeName !== "Lost_Outbound",
        )
        .reduce((subSum, type) => subSum + type.amount, 0);

      return sum + relevantAmount;
    },
    0,
  );

  return {
    totalAmount,
    typeSummary: unfilledClaim.summary.typeSummary ?? {},
  };
}

function buildLostOutboundKpi(
  unfilledClaim: UnClaimUiModel,
): LostOutboundClaimWidgetKpi {
  return {
    total: calculateLostByType(unfilledClaim, "Lost_Outbound", "total"),
    amount: calculateLostByType(unfilledClaim, "Lost_Outbound", "amount"),
  };
}

function buildStoreProviderReportRows(
  filedClaim: FiledClaimUiModel,
  unfilledClaim: UnClaimUiModel,
): ProviderReportData[] {
  const dataSource: ProviderReportData[] = [];

  filedClaim.filedClaimProviderBreakdownUIModel.forEach((provider) => {
    let claimSuccessRate = 0;
    if (provider.filedCasesTotal > 0) {
      const reimbursedCases =
        (provider.cashReimbursementAmount ?? 0) +
        (provider.inventoryReimbursementAmount ?? 0);
      const rate = (reimbursedCases / provider.filedCasesAmount) * 100;
      claimSuccessRate = Math.round(rate * 100) / 100;
    }

    dataSource.push({
      provider: provider.providerName,
      filedCases: provider.filedCasesTotal,
      claimSuccessRate,
      cashReimbursement: provider.cashReimbursementAmount,
      inventoryReimbursement: provider.inventoryReimbursementAmount,
      totalReimbursement:
        (provider.cashReimbursementAmount ?? 0) +
        (provider.inventoryReimbursementAmount ?? 0),
      unclaimedAmount: 0,
      unclaimedTotal: 0,
    });
  });

  unfilledClaim.unClaimProviderBreakdownUIModel.forEach((provider) => {
    const index = dataSource.findIndex(
      (item) => item.provider === provider.providerName,
    );
    if (index !== -1) {
      dataSource[index].unclaimedAmount = provider.unclaimedCasesAmount;
      dataSource[index].unclaimedTotal = provider.unclaimedCasesTotal;
    } else {
      dataSource.push({
        provider: provider.providerName,
        filedCases: 0,
        claimSuccessRate: 0,
        cashReimbursement: 0,
        inventoryReimbursement: 0,
        totalReimbursement: 0,
        unclaimedAmount: provider.unclaimedCasesAmount,
        unclaimedTotal: provider.unclaimedCasesTotal,
      });
    }
  });

  return dataSource;
}

function buildStoreProviderReportKpi(
  filedClaim: FiledClaimUiModel,
  unfilledClaim: UnClaimUiModel,
): StoreProviderReportWidgetKpi {
  const rows = buildStoreProviderReportRows(filedClaim, unfilledClaim);
  const totalCashReimbursement = rows.reduce(
    (sum, row) => sum + row.cashReimbursement,
    0,
  );
  const totalInventoryReimbursement = rows.reduce(
    (sum, row) => sum + row.inventoryReimbursement,
    0,
  );

  return {
    rows,
    totalCashReimbursement,
    totalInventoryReimbursement,
    totalReimbursement: totalCashReimbursement + totalInventoryReimbursement,
  };
}

export function buildDashboardWidgetKpis(
  filedClaim: FiledClaimUiModel,
  unfilledClaim: UnClaimUiModel,
  options: BuildDashboardWidgetKpisOptions,
): DashboardWidgetKpis {
  return {
    caseOverview: buildCaseOverviewKpi(filedClaim),
    caseStatus: buildCaseStatusKpi(filedClaim, options.caseStatuses),
    opportunityValue: buildOpportunityValueKpi(
      filedClaim,
      unfilledClaim,
      options.rangeLabel,
    ),
    otherClaims: buildOtherClaimsKpi(unfilledClaim),
    lostOutbound: buildLostOutboundKpi(unfilledClaim),
    storeProviderReport: buildStoreProviderReportKpi(filedClaim, unfilledClaim),
  };
}

export function createEmptyDashboardWidgetKpis(
  rangeLabel: string,
  caseStatuses: BuildDashboardWidgetKpisOptions["caseStatuses"] = [],
): DashboardWidgetKpis {
  return {
    caseOverview: {
      filedCasesAmount: 0,
      filedCasesTotal: 0,
      cashReimbursementTotal: 0,
      inventoryReimbursementTotal: 0,
      cashReimbursementAmount: 0,
      inventoryReimbursementAmount: 0,
    },
    caseStatus: {
      openAmount: 0,
      successRate: 0,
      statusSummary: {},
      caseStatuses,
    },
    opportunityValue: {
      rangeLabel,
      nonReimbursementTotal: 0,
      filedNoReimbursementTotal: 0,
      unFiledClaimAmount: 0,
      unFiledClaimTotal: 0,
      filedNoReimbursementAmount: 0,
    },
    otherClaims: {
      totalAmount: 0,
      typeSummary: {},
    },
    lostOutbound: {
      total: 0,
      amount: 0,
    },
    storeProviderReport: {
      rows: [],
      totalReimbursement: 0,
      totalCashReimbursement: 0,
      totalInventoryReimbursement: 0,
    },
  };
}
